import {
    AlertModal,
    Box,
    Button,
    Checkbox,
    Modal as ComponentsModal,
    Tooltip as ComponentsTooltip,
    EmptyState,
    External,
    IconButton,
    LinkText,
    Table,
    TextBlock,
    TextCell,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { TFunction } from "i18next";
import * as React from "react";
import { FileDrop } from "react-file-drop";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Column } from "react-table";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { DateRange } from "rsuite/DateRangePicker";
import "rsuite/dist/rsuite.min.css";
import { endOfDay, startOfDay, subDays } from "rsuite/esm/internals/utils/date";

import { ReportsTableActionBar } from "./action-bar/ReportsTableActionBar";
import {
    ColumnCreatorParams,
    createAutopilotEnrollmentQueryTimestampColumn,
    createAutopilotRecheckPreviousEnrollmentStatusColumn,
    createAutopilotRecheckStatusColumn,
    createAutopilotRecheckStatusDateColumn,
    createAutopilotRecheckTimestampColumn,
    createAutopilotStatusColumn,
    createCustomIntegrityVerifiedColumn,
    createEditedDateColumn,
    createErasureStatusColumn,
    createFaultCodeColumn,
    createFaultCodeExpirationDateColumn,
    createFaultCodeRunDateColumn,
    createFaultFoundColumn,
    createOverallFaultFoundColumn,
    createPriceColumn,
    createProductColumn,
    createReportDateColumn,
    createUserColumn,
    createVerifiedColumn,
} from "./AllReportsTableColumns";
import BulkSearchModal from "./BulkSearchModal";
import DiagnosticReportModal from "./DiagnosticReportModal";
import style from "./erasure-reports-table.scss";
import ErasureReportModal from "./ErasureReportModal";
import ExportReportsView from "./export/ExportReportsView";
import { ReportTableKebabMenu } from "./kebab-menu/ReportTableKebabMenu";
import ReportsSearchView from "./ReportsSearchView";
import { deriveReportUrl } from "./ReportView";
import CustomDatePickerView, {
    ALL_REPORTS_VIEW_IDENTIFIER,
    DateRangeSelection,
} from "components/common-date-picker/CustomDatePicker";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import ErrorMessage from "components/error-message/ErrorMessage";
import { eventBus } from "components/event-bus/eventBus";
import BulkSearchIcon from "components/icons/BulkSearchIcon";
import { ToastInfoIcon } from "components/icons/ToastInfoIcon";
import Warning from "components/icons/Warning";
import { subpageContext, viewActionsContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { ExportDetails } from "components/reports/AllReportsView";
import ReportRecheck from "components/reports/AutopilotRecheck";
import BulkReportDeletion from "components/reports/BulkReportDeletion";
import LoadReportPathsModal from "components/reports/LoadReportPathsModal";
import ShareReportDialog from "components/reports/ShareReportDialog";
import SingleReportDeletion from "components/reports/SingleReportDeletion";
import { HOME_ROUTE, PRODUCTS_ROUTE } from "components/router/Routes";
import QuickFilterButton from "components/table/QuickFilterButton";
import Tooltip from "components/tooltip/Tooltip";
import Heading from "components/typography/heading/Heading";
import { AUTH_REPORT_TEMPLATE_VIEW } from "domain/authority";
import {
    CustomReportView,
    CustomReportViewFilter,
    Path,
    PATH_TYPE_ONLY_STRING,
    QuickFilterButton as QuickFilterButtonType,
} from "domain/reports";
import { assetService } from "services/asset-management/AssetService";
import { useIsAiHomeEnabled } from "services/feature/useIsAiHomeEnabled";
import { getLanguage } from "services/language/languageRepository";
import { getOliverUrl } from "services/login/endpointRepository";
import { createTranslatorReportsPath } from "services/pathTranslator";
import { AutopilotDeviceIdentifier, autopilotService } from "services/report/AutopilotService";
import {
    deriveProductName,
    FetchAllReportsService,
    Report,
    ReportService,
    ReportType,
} from "services/report/erasure/ReportService";
import { reportTemplateService } from "services/report/erasure/ReportTemplateService";
import { reportExportService } from "services/report/ReportExportService";
import { reportViewService } from "services/report/ReportViewService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { getReportUuidsToBeDeleted, setReportUuidsToBeDeleted } from "services/table/tableRepository";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { ReportViewSettings } from "store/reportViewSettings";
import { TenantDetails } from "store/tenantDetails";
import buttonStyle from "styles/buttons.scss";
import layoutStyle from "styles/layout.scss";
import { resolveColor } from "utils/commonFunctions";
import { toUtcDateString, truncateWithEllipsis } from "utils/format";
import { logger } from "utils/logging";
import { getObject, RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

export interface TableState {
    cursor: string[];
    data: Report[];
    scrollPosition: number | undefined;
    searchQuery: string;
    sortColumn?: string;
    sortAscending: boolean;
    totalCount: number;
    startDate?: string;
    endDate?: string;
}

export interface LocationState {
    report?: Report;
    productType: string;
    toCreateViews: boolean;
}

export function isStringArray(array: unknown[]): array is string[] {
    return array.some((each) => typeof each === "string");
}

function deriveNullSortResponse(first: unknown | null, second: unknown | null): number | undefined {
    if (first == null && second == null) {
        return 0;
    }
    if (first == null) {
        return 1;
    }
    if (second == null) {
        return -1;
    }
}

/**
 * Sort strings case insensitively with null values last.
 *
 * @param array to be sorted.
 * @param ascending if undefined, sort is skipped.
 * @returns the input array if ascending is undefined, otherwise a sorted copy.
 */
export function sortStrings(array: string[], ascending?: boolean): string[] {
    if (ascending == null) {
        return array;
    }
    // It's better to not modify input parameters so copy the array first.
    return [...array].sort((first, second) => {
        const nullResponse = deriveNullSortResponse(first, second);
        if (nullResponse != null) {
            return nullResponse;
        }
        return ascending ? first.localeCompare(second) : second.localeCompare(first);
    });
}

/**
 * Sort numbers with null values last.
 *
 * @param array to be sorted.
 * @param ascending if undefined, sort is skipped.
 * @returns the input array if ascending is undefined, otherwise a sorted copy.
 */
export function sortNumbers(array: number[], ascending?: boolean): number[] {
    if (ascending == null) {
        return array;
    }
    // It's better to not modify input parameters so copy the array first.
    return [...array].sort((first, second) => {
        const nullResponse = deriveNullSortResponse(first, second);
        if (nullResponse != null) {
            return nullResponse;
        }
        return ascending ? first - second : second - first;
    });
}

const DISK_PATHS = [
    "blancco_erasure_report.disks.disk.capacity",
    "blancco_erasure_report.erasures.erasure.target.capacity",
    "blancco_hardware_report.disks.disk.blocksize",
    "blancco_hardware_report.disks.disk.capacity",
    "blancco_hardware_report.disks.disk.region.capacity",
    "blancco_hardware_report.disks.disk.region.sub_region.capacity",
];
const MEMORY_PATHS = [
    "blancco_hardware_report.memory.accessible_memory",
    "blancco_hardware_report.memory.memory_bank.capacity",
    "blancco_hardware_report.memory.physical_memory",
    "blancco_hardware_report.memory.total_memory",
    "blancco_hardware_report.system.ram",
    "blancco_hardware_report.system.total_memory",
    "blancco_hardware_report.video_cards.video_card.video_memory",
];
const FREQUENCY_PATHS = [
    "blancco_hardware_report.processors.processor.frequency",
    "blancco_hardware_report.memory.memory_bank.hz",
    "blancco_hardware_report.processors.processor.max_frequency",
    "blancco_hardware_report.processors.processor.nominal_frequency",
];

export const MAX_PRINT_REPORTS_LIMIT = 20;
export const MAX_RECHECK_REPORTS_LIMIT = 200;
export const MAX_SHARE_REPORTS_LIMIT = 1_000;

interface IsoUnitPath {
    base: number;
    suffix: "Hz" | "B";
}

const PATH_TO_ISO_UNIT = new Map<string, IsoUnitPath>([
    ...DISK_PATHS.map((each) => [each, { base: 1000, suffix: "B" }] as const),
    ...MEMORY_PATHS.map((each) => [each, { base: 1024, suffix: "B" }] as const),
    ...FREQUENCY_PATHS.map((each) => [each, { base: 1000, suffix: "Hz" }] as const),
]);

export function convertToStorageCapacity(path: string, values: number[]): string[] {
    const isoUnit = PATH_TO_ISO_UNIT.get(path);
    if (isoUnit == null) {
        return values.map((each) => each.toString());
    }

    const prefixes = ["", "K", "M", "G", "T", "P", "E", "Z", "Y"];
    return values.map((value) => {
        if (value === 0) {
            return `0 ${isoUnit.suffix}`;
        }
        const exp = Math.floor(Math.log(value) / Math.log(isoUnit.base));
        const divided = (value / Math.pow(isoUnit.base, exp)).toFixed();
        return `${divided} ${prefixes[exp]}${isoUnit.suffix}`;
    });
}

export function convertToStandardUnit(path: string, value: number, unit: string): number {
    const isoUnit = PATH_TO_ISO_UNIT.get(path);
    if (isoUnit == null) {
        return value;
    }
    const prefixes: { [key: string]: number } = {
        "": 0,
        K: 1,
        M: 2,
        G: 3,
        T: 4,
        P: 5,
        E: 6,
        Z: 7,
        Y: 8,
    };
    const firstChar = unit.charAt(0).toUpperCase();
    const prefix: string = firstChar === "B" ? "" : firstChar;
    const exp = prefixes[prefix];
    if (!exp) {
        return value;
    }
    return value * Math.pow(isoUnit.base, exp);
}

function isRowDisabled(uuid: string): boolean {
    return getReportUuidsToBeDeleted().includes(uuid);
}

export function createPathStringCell(
    path: string,
    translatePath: (path: string) => string,
    sortable: boolean,
    sortAscending?: boolean
): Column<Report> {
    interface PathStringCell {
        pathValue: string;
        uuid: string;
    }
    return {
        Header: <TextCell>{translatePath(path)}</TextCell>,
        accessor: (report: Report): PathStringCell => {
            const entry = report.entries.find((e) => e.path === path);
            const values = entry != null ? entry.values : null;
            if (Array.isArray(values) && values.length > 0) {
                const sorted = isStringArray(values)
                    ? sortStrings(values, sortAscending)
                    : convertToStorageCapacity(path, sortNumbers(values, sortAscending));
                return { pathValue: sorted.join(", "), uuid: report.uuid };
            }
            return {
                pathValue: "",
                uuid: report.uuid,
            };
        },
        id: path,
        width: 250,
        Cell: ({ cell: { value: value } }: { cell: { value: PathStringCell } }) => (
            <TextCell disabled={isRowDisabled(value.uuid)}>{value.pathValue}</TextCell>
        ),
        disableSortBy: !sortable,
    };
}

export function deriveSearchHint(tableState: TableState, loading: boolean, t: TFunction): JSX.Element | null {
    if (loading || tableState.data.length <= 0) {
        return null;
    }

    const text = t("ErasureReportsTable.reportCountLabel", {
        shownCount: tableState.data.length,
        totalCount: tableState.totalCount,
    });
    return (
        <div className={style.count} data-testid={testIds.common.primaryView.table.resultSummaryContainer}>
            {text}
        </div>
    );
}

function convertToReportTypeLabel(reportType: ReportType): Label | undefined {
    const pairs: [ReportType, Label][] = [
        ["ERASURE", Label.ERASURE],
        ["DIAGNOSTIC", Label.DIAGNOSTIC],
    ];
    for (const each of pairs) {
        if (reportType === each[0]) {
            return each[1];
        }
    }
}

export function buildReportViewUrl(reportUuid: string, templateUuid?: string) {
    return getOliverUrl() + deriveReportUrl(reportUuid, getLanguage().code, "HTML", templateUuid);
}
function openReportInNewTab(reportUuid: string, templateUuid?: string) {
    window.open(buildReportViewUrl(reportUuid, templateUuid), "_blank");
}

function openReportInNewTabWithDefaultTemplate(reportUuid: string, tenantDetails?: TenantDetails) {
    if (
        tenantDetails?.featureLicenses.includes("FEATURE_REPORT_CUSTOMIZATION") &&
        userSessionService.userHasAllAuthorities([AUTH_REPORT_TEMPLATE_VIEW])
    ) {
        reportTemplateService
            .fetchTemplates()
            .then((templates) => {
                const defaultTemplate = templates.find((t) => t.defaultReportTemplate.own);
                openReportInNewTab(reportUuid, defaultTemplate?.uuid);
            })
            .catch(() => {
                openReportInNewTab(reportUuid);
            });
    } else {
        openReportInNewTab(reportUuid);
    }
}

function createReportModal(
    report: Report,
    hide: () => void,
    onReportDelete: () => void,
    viewDetails?: CustomReportView
): JSX.Element | null {
    if (report.reportType === "ERASURE") {
        return (
            <ErasureReportModal
                reportUuid={report.uuid}
                date={report.date}
                hide={hide}
                openInNewTab={(templateUuid) => openReportInNewTab(report.uuid, templateUuid)}
                onReportDelete={onReportDelete}
                viewDetails={viewDetails}
            />
        );
    }
    if (report.reportType === "DIAGNOSTIC") {
        return (
            <DiagnosticReportModal
                diagnosticData={ReportService.toDiagnosticData(report)}
                hide={hide}
                onReportDelete={onReportDelete}
            />
        );
    }
    return null;
}

function deriveStartDate(viewSettings: ReportViewSettings, defaultRange: DateRange) {
    if (!viewSettings) {
        return toUtcDateString(defaultRange[0]);
    }
    return viewSettings.selectedRange == DateRangeSelection.ALL ? undefined : viewSettings.startDate;
}

function deriveEndDate(viewSettings: ReportViewSettings, defaultRange: DateRange) {
    if (!viewSettings) {
        return toUtcDateString(defaultRange[1]);
    }
    return viewSettings.selectedRange == DateRangeSelection.ALL ? undefined : viewSettings.endDate;
}

function sendColumnSortUsageStatisticsEvent(column: string | null) {
    usageStatisticsService.sendEvent({
        category: Category.REPORTS,
        action: Action.CHANGE_COLUMN_SORT,
        label: column ?? "none",
    });
}

function findSelectedReportUuids(reports: Report[]): string[] {
    const foundUuids: string[] = [];
    reports.forEach((each) => {
        if (each.checked) {
            foundUuids.push(each.uuid);
        }
    });
    return foundUuids;
}

function findSelectedShareableReportUuids(reports: Report[]): string[] {
    return reports.filter((each) => each.checked && each.reportType === "ERASURE").map((each) => each.uuid);
}

function findSelectedAutopilotIdentifiers(reports: Report[]): AutopilotDeviceIdentifier[] {
    return filterUniqueAutopilotIdentifiers(
        reports
            .filter((e) => e.checked)
            .map((e) => extractAutopilotDeviceIdentifier(e))
            .filter((e) => e != null)
    );
}

function computeTotalSelectedReportsCount(allReports: Report[]) {
    return findSelectedReportUuids(allReports).length;
}

const mapState = (state: StoreState) => ({
    settings: state.reportViewSettings,
    theme: state.themeReducer.theme,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
});
const connector = connect(mapState);

function AllReportsTable(
    props: {
        fetchAllReportsService: FetchAllReportsService;
        viewDetails?: CustomReportView;
        onReportDelete: () => void;
        onDetailsChanged: (exportDetails: ExportDetails) => void;
        count: number;
        refreshCount: number;
        setExportVisibility: (visibility: boolean) => void;
        setTotalReportCount: (count: number) => void;
        setDisableExportReportButton?: (multiSelect: boolean) => void;
        filters?: CustomReportViewFilter[];
        showForm: (value: boolean) => void;
        hideDialogView: React.Dispatch<React.SetStateAction<boolean>>;
        editReportUuid: (value: string) => void;
        hasOnlyBmsReports?: boolean;
        hideEdit?: boolean;
        quickFilterButtons?: QuickFilterButtonType[];
        setLicenseCountVisibility?: (visibility: boolean) => void;
        availableLicenses?: number;
        onLicenseUpdate?: (newCount: number) => void;
    } & ConnectedProps<typeof connector>
): JSX.Element {
    const [loading, setLoading] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const defaultDateRange: DateRange = [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())];
    const viewIdentifier = props.viewDetails?.uuid || ALL_REPORTS_VIEW_IDENTIFIER;
    const viewSettings = props.settings[viewIdentifier];
    const [tableState, setTableState] = React.useState<TableState>(() => {
        const saved = getObject<{ sortBy?: Array<{ id: string; desc: boolean }> }>(RepositoryKey.ALL_REPORTS_TABLE);
        const initialSort = saved?.sortBy?.[0];
        return {
            data: [],
            cursor: [],
            scrollPosition: undefined,
            searchQuery: "",
            sortColumn: initialSort?.id,
            sortAscending: initialSort !== undefined ? !initialSort.desc : false,
            totalCount: 0,
            startDate: deriveStartDate(viewSettings, defaultDateRange),
            endDate: deriveEndDate(viewSettings, defaultDateRange),
        };
    });
    // This reference is used when creating the column with the checkboxes for selecting reports to delete.
    // Using just the state was not working as expecting when checkboxes need to be updated after fetching more data.
    // This was because the table state was living in the past when the method to create the column was called again,
    // meaning that the new reports after loading more were not part of the state. So I (Gina) created this
    // reference to the state so that when the checkboxes are updated, we can go around the issue with accessing
    // the changing state from the JavaScript context, that never updates from one render to the next one.
    const allReportsReference = React.useRef<Report[]>(tableState.data);
    const [deselected, setDeselected] = React.useState(false);
    const updateDateRange = (startDate?: string, endDate?: string) => {
        if (tableState.startDate === startDate && tableState.endDate === endDate) {
            return;
        }
        setTableState((previous) =>
            Object.assign({}, previous, {
                cursor: [],
                startDate: startDate,
                endDate: endDate,
            })
        );
        setInitialLoading(true);
    };
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [selectedReport, setSelectedReport] = React.useState<Report | null>(null);
    const [iframeLoaded, setIframeLoaded] = React.useState(false);
    const [iframeLoading, setIframeLoading] = React.useState(false);
    const [printError, setPrintError] = React.useState(false);
    const location = useLocation();
    const history = useHistory();
    const isAiHomeEnabled = useIsAiHomeEnabled();
    const toCreateViews = location.state ? (location.state as LocationState).toCreateViews : null;
    const report = location.state && !toCreateViews ? (location.state as LocationState).report : null;
    const productType = location.state && !toCreateViews ? (location.state as LocationState).productType : null;
    const context = React.useContext(subpageContext);
    const viewActions = React.useContext(viewActionsContext);
    const iframe = React.useRef(null);
    const tableRef = React.useRef(null);

    const removeDuplicates = (data: Report[]): Report[] => {
        let uniqueData = Array.from(new Set(data.map((item) => item.uuid))).map((uuid) => {
            return data.find((item) => item.uuid === uuid) ?? data[0];
        });

        if (tableState.sortColumn === Path.REPORT_OWNER || tableState.sortColumn === Path.REPORT_EDITOR) {
            uniqueData = sortReportsByUser(uniqueData, tableState.sortColumn, tableState.sortAscending);
        }

        return uniqueData;
    };

    const deselectAll = () => {
        setTableState((prevState) => ({
            ...prevState,
            data: prevState.data.map((each) => {
                if (each.checked) {
                    each.checked = false;
                }
                return each;
            }),
        }));
        setDeselected(true);
    };

    const selectAll = () => {
        setTableState((prevState) => ({
            ...prevState,
            data: prevState.data.map((each) => {
                if (!each.checked) {
                    each.checked = true;
                }
                return each;
            }),
        }));
    };

    const openExportModal = () => {
        setOnlyBmsReports(!tableState.data.some((each) => each.checked && each.reportType === "ERASURE"));
        setOpenExportDialog(true);
    };

    const handleAssetsRegistration = () => {
        const selectedReportUuids = findSelectedReportUuids(tableState.data);
        setSelectedReportCountForRegistration(selectedReportUuids.length);
        setRegistrationInProgressDialog(true);
        deselectAll();
        assetService.registerAssetsFromReports(selectedReportUuids).catch((error) => {
            logger.error("Failed to register assets from reports:", error);
            setRegistrationFailedDialog(true);
        });
    };

    const openPrintPreview = () => {
        tableState.data.some((each) => each.checked && each.reportType === "DIAGNOSTIC")
            ? setPrintConfirmationDialog(true)
            : printReports();
    };

    const openDeleteModal = () => {
        if (findSelectedReportUuids(tableState.data).length > 1) {
            setBulkDeletionInvoked(true);
        } else {
            setSingleDeletionInvoked(true);
        }
    };

    const sortReportsByUser = (reports: Report[], path: Path, sortAscending: boolean): Report[] =>
        reports.sort((a, b) => {
            const getValue = (report: Report): string => {
                const entry = report.entries.find((e) => e.path === path);
                return entry ? String(entry.values[0]) : "";
            };
            const compareResult = getValue(a)
                .trim()
                .localeCompare(getValue(b).trim(), undefined, { sensitivity: "base" });
            return sortAscending ? compareResult : -compareResult;
        });

    const fetchData = (reinitialized: boolean) => {
        if (toCreateViews) {
            context?.setSubpage({
                content: <LoadReportPathsModal setVisible={() => true} visible={true} />,
                isModal: false,
            });
        }
        if (reinitialized) {
            setInitialLoading(true);
            setReportUuidsToBeDeleted([]);
        } else {
            setLoading(true);
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        props.fetchAllReportsService
            .fetchAllReports({
                abortController,
                cursor: reinitialized ? [] : tableState.cursor,
                search: tableState.searchQuery,
                sortColumn: tableState.sortColumn,
                sortAscending: tableState.sortAscending,
                startDate: tableState.startDate,
                endDate: tableState.endDate,
                paths: props.viewDetails?.columns ?? [],
                filters: props.viewDetails?.filters,
            })
            .then((response) => {
                allReportsReference.current = reinitialized
                    ? removeDuplicates(response.reports)
                    : removeDuplicates(tableState.data.concat(response.reports));

                setTableState((previous) =>
                    Object.assign({}, previous, {
                        cursor: response.cursor,
                        data: (reinitialized ? [] : removeDuplicates(previous.data)).concat(
                            removeDuplicates(response.reports)
                        ),
                        scrollPosition: reinitialized ? undefined : previous.data.length - 1,
                        totalCount: response.total,
                    })
                );
                props.setExportVisibility(allReportsReference.current.length > 0);
                props.setLicenseCountVisibility?.(allReportsReference.current.length > 0);
                props.setTotalReportCount(parseInt(String(response.total)));
                props.onDetailsChanged({
                    startDate: tableState.startDate,
                    endDate: tableState.endDate,
                    searchQuery: tableState.searchQuery,
                });
                setLoading(false);
                showOnboardingToast();
                // Without this, tooltips appear only for the initially loaded
                // rows. I.e. not for those loaded with "Load more".
                ReactTooltip.rebuild();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("ErasureReportsTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
                setInitialLoading(false);
            });
    };
    React.useEffect(() => {
        if (report) {
            setSelectedReport(report);
        }
    }, []);

    const showOnboardingToast = () => {
        if (report === undefined) {
            toast(
                <ToastInfoIcon
                    containerClass={layoutStyle.customToastContainer}
                    linkClass={layoutStyle.customToastLink}
                    infoClass={layoutStyle.customToastInfoIcon}
                    color={props.theme.contentBackgroundColor}
                    text={t("ErasureReportsTable.returnToOnboarding")}
                    textButtonStyle={buttonStyle.textButton}
                    textGoBack={t("ErasureReportsTable.goBack")}
                    toastLink={layoutStyle.customToastLink}
                    onClick={() => {
                        goBackToOnboarding();
                    }}
                />,
                {
                    closeButton: false,
                    className: layoutStyle.customToastInfoMessage,
                    autoClose: false,
                }
            );
        }
    };

    const goBackToOnboarding = () => {
        history.push(isAiHomeEnabled ? PRODUCTS_ROUTE.path : HOME_ROUTE.path, {
            showGettingStarted: true,
            productType: productType,
        });
    };

    React.useEffect(() => {
        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [
        props.count,
        tableState.searchQuery,
        tableState.sortColumn,
        tableState.sortAscending,
        tableState.startDate,
        tableState.endDate,
        props.refreshCount,
    ]);

    function disableSort() {
        if (tableState.sortColumn == null) {
            return;
        }
        setTableState((previous) =>
            Object.assign({}, previous, {
                cursor: [],
                sortColumn: undefined,
            })
        );
        setInitialLoading(true);
        sendColumnSortUsageStatisticsEvent(null);
    }

    function changeSort(column: string, ascending: boolean | undefined): void {
        if (ascending === undefined) {
            return;
        }
        if (tableState.sortColumn === column && tableState.sortAscending === ascending) {
            return;
        }
        setTableState((previous) =>
            Object.assign({}, previous, {
                cursor: [],
                sortColumn: column,
                sortAscending: ascending,
            })
        );
        setInitialLoading(true);
        sendColumnSortUsageStatisticsEvent(column);
    }

    const { t } = useTranslation();

    const createUuidColumn = (): Column<Report> => {
        return {
            Header: <TextCell>{t("Common.uuid")}</TextCell>,
            accessor: "uuid",
            minWidth: 370,
            Cell: ({
                cell: {
                    row: { original: report },
                },
            }) => {
                const handler = (event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent) => {
                    usageStatisticsService.sendEvent({
                        category: Category.REPORTS,
                        action: Action.VIEW_REPORT_PROPERTIES,
                        label: convertToReportTypeLabel(report.reportType),
                    });
                    if (event.ctrlKey && report.reportType === "ERASURE") {
                        openReportInNewTabWithDefaultTemplate(report.uuid, props.tenantDetails);
                    } else {
                        setSelectedReport(report);
                    }
                };
                return (
                    <div
                        className={classNames(style.flexDisplay, style.reportUuid, {
                            [layoutStyle.disabledContainer]: isRowDisabled(report.uuid),
                        })}
                    >
                        <TextCell>
                            <LinkText as="button" onClick={(event) => handler(event)}>
                                {report.uuid}
                            </LinkText>
                        </TextCell>
                        <div
                            className={style.quickActions}
                            data-testid={testIds.common.primaryView.table.quickMenu.itself}
                        >
                            <CopyToClipboard
                                value={report.uuid}
                                testId={testIds.workArea.report.primaryView.table.quickMenu.copyButton}
                                isNewTable
                            />
                            {report.reportType === "ERASURE" ? (
                                <ComponentsTooltip>
                                    <TooltipContent testId={testIds.common.primaryView.table.cellTooltip.itself}>
                                        {t("Common.openInNewTab")}
                                    </TooltipContent>
                                    <TooltipTrigger testId={testIds.common.primaryView.table.cellTooltip.trigger}>
                                        <IconButton
                                            onClick={() =>
                                                openReportInNewTabWithDefaultTemplate(report.uuid, props.tenantDetails)
                                            }
                                            testId={
                                                testIds.workArea.report.primaryView.table.quickMenu.openInNewTabButton
                                            }
                                        >
                                            <External size={24} color={resolveColor("--theme-icon-fill-default")} />
                                        </IconButton>
                                    </TooltipTrigger>
                                </ComponentsTooltip>
                            ) : (
                                ""
                            )}
                        </div>
                    </div>
                );
            },
        };
    };

    const createFirstColumn = (): Column<Report> => {
        const isUuidSelected = (uuid: string): boolean => {
            return (
                typeof allReportsReference.current.find(
                    (each) => each.uuid === uuid && each.checked && !each.disabled
                ) !== "undefined"
            );
        };
        const hasAnyReportSelected = (): boolean => {
            return typeof allReportsReference.current.find((each) => each.checked && !each.disabled) !== "undefined";
        };

        const hasAllReportsSelected = (): boolean => {
            const foundReports: Report[] = [];
            const onlyNonDisabledReports: Report[] = [];
            allReportsReference.current.forEach((each) => {
                if (typeof each.disabled === "undefined" || !each.disabled) {
                    if (each.checked) {
                        foundReports.push(each);
                    }
                    onlyNonDisabledReports.push(each);
                }
            });
            return onlyNonDisabledReports.length == foundReports.length;
        };
        const updateTableStateBasedOnUuid = (checkedValue: boolean, selectedUuid: string) => {
            setTableState((prevState) => ({
                ...prevState,
                data: prevState.data.map((each) => {
                    if (each.uuid === selectedUuid) {
                        each.checked = checkedValue;
                    }
                    return each;
                }),
                scrollPosition: undefined,
            }));
        };

        const updateReportState = (checkedValue: boolean) => {
            setTableState((prevState) => ({
                ...prevState,
                data: prevState.data.map((each) => {
                    if (!each.disabled) {
                        each.checked = checkedValue;
                    }
                    return each;
                }),
            }));
        };
        const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, uuid: string) => {
            setDeselected(false);
            if (event.target.checked) {
                updateTableStateBasedOnUuid(true, uuid);
            } else {
                updateTableStateBasedOnUuid(false, uuid);
            }
        };
        const createSelectionCheckbox = (uuid: string) => {
            return (
                <Checkbox
                    key={uuid}
                    name="uuids"
                    id={uuid}
                    checked={isUuidSelected(uuid) && !deselected && !isRowDisabled(uuid)}
                    value={uuid}
                    onChange={(e) => handleCheckboxChange(e, uuid)}
                    hideLabelSection={true}
                    testIds={{ input: testIds.common.primaryView.table.selectRowCheckbox }}
                />
            );
        };

        return {
            Header: () => {
                return (
                    <Checkbox
                        name="selectAllCheckbox"
                        checked={hasAllReportsSelected()}
                        onChange={(event) => {
                            updateReportState(event.target.checked ? true : !hasAllReportsSelected());
                        }}
                        hideLabelSection={true}
                        indeterminate={hasAnyReportSelected() && !hasAllReportsSelected()}
                        testIds={{ input: testIds.common.primaryView.table.selectAllCheckbox }}
                    />
                );
            },
            accessor: "checked",
            disableSortBy: true,
            minWidth: 85,
            maxWidth: 85,
            Cell: ({
                cell: {
                    row: { original: report },
                },
            }) => {
                const autopilotDeviceIdentifier = extractAutopilotDeviceIdentifier(report);

                return (
                    <Box gap={"Xxs"} verticalAlignment={"Center"}>
                        {createSelectionCheckbox(report.uuid)}
                        <ReportTableKebabMenu
                            reportUuid={report.uuid}
                            onReportDeleted={props.onReportDelete}
                            showForm={props.showForm}
                            reportType={report.reportType}
                            editReportUuid={props.editReportUuid}
                            hideEdit={props.hideEdit}
                            autopilotDeviceIdentifier={autopilotDeviceIdentifier}
                            onRecheckComplete={closeRecheckModal}
                            tableRef={tableRef}
                        />
                    </Box>
                );
            },
        };
    };

    const translatePath = createTranslatorReportsPath(useTranslation);
    const createColumns = (paths: string[]): Column<Report>[] => {
        const columns: Column<Report>[] = [];
        const columnParams: ColumnCreatorParams = {
            t,
            translatePath,
        };
        columns.push(createFirstColumn());
        const pathToType = reportViewService
            .getPaths()
            .reduce((map, each) => map.set(each.path, each.pathType), new Map<string, string>());
        for (const path of paths) {
            switch (path) {
                case "uuid":
                    columns.push(createUuidColumn());
                    break;
                case "product":
                    columns.push(createProductColumn(columnParams));
                    break;
                case "creationDate":
                case "date":
                    columns.push(createReportDateColumn(columnParams));
                    break;
                case Path.EDITED_DATE:
                    columns.push(createEditedDateColumn(columnParams));
                    break;
                case Path.ERASURE_STATE:
                    columns.push(createErasureStatusColumn(columnParams));
                    break;
                case Path.VERIFIED:
                    columns.push(createVerifiedColumn(columnParams));
                    break;
                case Path.CUSTOM_INTEGRITY_VERIFIED:
                    columns.push(createCustomIntegrityVerifiedColumn(columnParams));
                    break;
                case Path.NTF_RESULT_FAULT_CODE:
                    columns.push(createFaultCodeColumn(columnParams));
                    break;
                case Path.NTF_RESULT_FAULT_FOUND:
                    columns.push(createFaultFoundColumn(columnParams));
                    break;
                case Path.NTF_OVERALL_FAULT_FOUND:
                    columns.push(createOverallFaultFoundColumn(columnParams));
                    break;
                case Path.NTF_RESULT_REPORT_DATE:
                    columns.push(createFaultCodeRunDateColumn(columnParams));
                    break;
                case Path.NTF_OVERALL_REFERENCE_CODE_EXPIRATION:
                    columns.push(createFaultCodeExpirationDateColumn(columnParams));
                    break;
                case "bbti.quote.price":
                case "bbti.estimate.price":
                    columns.push(createPriceColumn(path, columnParams));
                    break;
                case Path.REPORT_OWNER:
                    columns.push(createUserColumn("ErasureReportsTable.reportOwner", Path.REPORT_OWNER, columnParams));
                    break;
                case Path.REPORT_EDITOR:
                    columns.push(
                        createUserColumn("ErasureReportsTable.reportEditor", Path.REPORT_EDITOR, columnParams)
                    );
                    break;
                case Path.AUTOPILOT_STATE:
                    columns.push(createAutopilotStatusColumn(columnParams));
                    break;
                case Path.AUTOPILOT_ENROLLMENT_QUERY_TIMESTAMP:
                    columns.push(createAutopilotEnrollmentQueryTimestampColumn(columnParams));
                    break;
                case Path.AUTOPILOT_RECHECK_STATUS:
                    columns.push(createAutopilotRecheckStatusColumn(columnParams));
                    break;
                case Path.AUTOPILOT_RECHECK_STATUS_DATE:
                    columns.push(createAutopilotRecheckStatusDateColumn(columnParams));
                    break;
                case Path.AUTOPILOT_RECHECK_TIMESTAMP:
                    columns.push(createAutopilotRecheckTimestampColumn(columnParams));
                    break;
                case Path.AUTOPILOT_RECHECK_PREVIOUS_ENROLLMENT_STATUS:
                    columns.push(createAutopilotRecheckPreviousEnrollmentStatusColumn(columnParams));
                    break;
                default:
                    columns.push(
                        createPathStringCell(
                            path,
                            translatePath,
                            pathToType.get(path) !== PATH_TYPE_ONLY_STRING,
                            tableState.sortColumn == null ? undefined : tableState.sortAscending
                        )
                    );
            }
        }
        return columns;
    };
    const columns: Column<Report>[] = createColumns(props.viewDetails?.columns ?? []);
    const searchHint = requestFailureMessage === "" ? deriveSearchHint(tableState, loading || initialLoading, t) : null;
    let emptyMessage;
    if (requestFailureMessage === "" && !loading) {
        emptyMessage =
            tableState.searchQuery === ""
                ? t("ErasureReportsTable.emptyStateMessage")
                : t("Common.searchEmptyStateMessage", { searchQuery: truncateWithEllipsis(tableState.searchQuery) });
    }

    const modal =
        selectedReport == null
            ? null
            : createReportModal(
                  selectedReport,
                  () => {
                      setSelectedReport(null);
                      if (report != null && productType != null) {
                          goBackToOnboarding();
                      }
                  },
                  props.onReportDelete,
                  props.viewDetails
              );

    const uuid = (() => {
        if (props.viewDetails == null) {
            return undefined;
        }
        const uuid = props.viewDetails.uuid;
        return uuid.length > 0 ? uuid : undefined;
    })();
    const table = (
        <Table
            columns={columns}
            data={tableState.data}
            tableIdentity={RepositoryKey.ALL_REPORTS_TABLE}
            emptyStateMessage={<EmptyState text={emptyMessage} viewActions={viewActions} />}
            failureMessage={requestFailureMessage}
            loaded={!initialLoading}
            scrollTo={tableState.scrollPosition}
            testIds={{ itself: testIds.workArea.report.primaryView.table.itself }}
            tooltips={true}
            containerRef={tableRef}
            sortCallbacks={{
                onChange: changeSort,
                onDisable: disableSort,
            }}
            sortTooltipTexts={{
                ascending: t("Common.ascending"),
                descending: t("Common.descending"),
                default: t("Common.sortContent"),
            }}
        />
    );

    function handleFileDrop(files: FileList) {
        if (files.length <= 0) {
            return;
        }
        eventBus.getReportImportFileTopic().publish({ files: Array.from(files) });
    }

    const [openExportDialog, setOpenExportDialog] = React.useState(false);
    const [onlyBmsReports, setOnlyBmsReports] = React.useState(false);
    const [bulkDeletionInvoked, setBulkDeletionInvoked] = React.useState(false);
    const [shareDialogVisible, setShareDialogVisible] = React.useState(false);
    const [showAutopilotRecheck, setShowAutopilotRecheck] = React.useState(false);
    const [hasValidLicensesForRecheck, setHasValidLicensesForRecheck] = React.useState(false);
    const [bulkDeletionDone, setBulkDeletionDone] = React.useState(false);
    const [singleDeletionInvoked, setSingleDeletionInvoked] = React.useState(false);
    const [printConfirmationDialog, setPrintConfirmationDialog] = React.useState(false);
    const [bulkModalOpen, setBulkModalOpen] = React.useState(false);
    const [registrationInProgressDialog, setRegistrationInProgressDialog] = React.useState(false);
    const [registrationFailedDialog, setRegistrationFailedDialog] = React.useState(false);
    const [selectedReportCountForRegistration, setSelectedReportCountForRegistration] = React.useState(0);

    React.useEffect(() => {
        autopilotService.hasValidLicensesForRecheck().then(setHasValidLicensesForRecheck);
    }, []);

    React.useEffect(() => {
        if (bulkDeletionDone) {
            setTableState((prevState) => ({
                ...prevState,
                data: prevState.data.map((each) => {
                    if (each.checked) {
                        each.checked = false;
                        each.disabled = true;
                    }
                    return each;
                }),
            }));
        }
        setBulkDeletionDone(false);
    }, [bulkDeletionDone]);

    const tableWrapper = hasTenantCookie() ? table : <FileDrop onDrop={handleFileDrop}>{table}</FileDrop>;

    const printReports = () => {
        setIframeLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        reportExportService
            .printReports(findSelectedReportUuids(tableState.data), "HTML", getLanguage().code, abortController)
            .then((body) => {
                const current = iframe.current;
                if (current == null) {
                    logger.error("Unable to load report for printing.");
                    return;
                }
                const iframeDocument = (current as HTMLIFrameElement).contentDocument;
                if (iframeDocument == null) {
                    return;
                }
                iframeDocument.write(body);
                iframeDocument.close();
            })
            .catch((reason) => {
                logger.error(reason);
                setPrintError(true);
            })
            .finally(() => {
                setIframeLoading(false);
            });
    };

    React.useEffect(() => {
        if (iframeLoaded) {
            const current = iframe.current;
            if (current == null) {
                logger.error("Unable to load report for printing.");
                return;
            }
            const iframeDocument = (current as HTMLIFrameElement).contentDocument;
            if (iframeDocument == null) {
                return;
            }
            if (iframeDocument.body.innerHTML !== "") {
                const iframeWindow = (current as HTMLIFrameElement).contentWindow;

                iframeWindow?.focus();
                iframeWindow?.print();
            }
            setIframeLoaded(false);
        }
    }, [iframeLoaded]);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    });

    const erasureReportCount = tableState.data.filter((each) => each.checked && each.reportType === "ERASURE").length;

    const isReportSelected = () => {
        const length = computeTotalSelectedReportsCount(tableState.data);
        props.setDisableExportReportButton && props.setDisableExportReportButton(length > 0);
        return length > 0;
    };
    const isReportWithAutopilotStateSelected = React.useCallback(() => {
        return tableState.data.some((report) => report.checked && extractAutopilotDeviceIdentifier(report) != null);
    }, [tableState]);

    const selectionCountOverPrintLimit = erasureReportCount > MAX_PRINT_REPORTS_LIMIT;
    const selectionCountOverShareLimit =
        findSelectedShareableReportUuids(tableState.data).length > MAX_SHARE_REPORTS_LIMIT;
    const selectionCountOverRecheckLimit =
        tableState.data.filter((each) => each.checked && extractAutopilotDeviceIdentifier(each) != null).length >
        MAX_RECHECK_REPORTS_LIMIT;

    const closeRecheckModal = (licensesConsumed: number) => {
        setShowAutopilotRecheck(false);
        props.onLicenseUpdate?.(licensesConsumed);
    };

    const applyBulkSearch = (joined: string) => {
        setBulkModalOpen(false);
        if (tableState.searchQuery === joined.trim()) {
            return;
        }
        setTableState((previous) =>
            Object.assign({}, previous, {
                cursor: [],
                searchQuery: joined.trim(),
            })
        );
        setInitialLoading(true);
    };

    return (
        <>
            <div className={classNames(style.tableDetailsGrid, style.gridContentLayout)}>
                <Box
                    verticalAlignment="Center"
                    gap="Md"
                    className={classNames(!props.quickFilterButtons ? style.tableDetailsDirection : style.wrapElements)}
                >
                    {props.quickFilterButtons && (
                        <Box gap="Xxxs" className={style.quickFilterButtonsContainer}>
                            {props.quickFilterButtons.map((button, index) => (
                                <div key={index} className={style.quickFilterButtonContainer}>
                                    <QuickFilterButton
                                        filter={button.text}
                                        isActive={button.isSelected}
                                        onClick={() => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.REPORTS,
                                                action: Action.SELECT_QUICK_FILTER,
                                                label: button.identifier,
                                            });
                                            button.onSelected();
                                        }}
                                        testId={button.testId}
                                    />
                                </div>
                            ))}
                        </Box>
                    )}
                    {searchHint}
                </Box>

                <div className={style.filter}>
                    <div className={style.search}>
                        <ReportsSearchView
                            setReportsSearch={(searchQuery: string) => {
                                if (tableState.searchQuery === searchQuery) {
                                    return;
                                }
                                setTableState((previous) =>
                                    Object.assign({}, previous, {
                                        cursor: [],
                                        searchQuery: searchQuery.trim(),
                                    })
                                );
                                setInitialLoading(true);
                            }}
                            searchInProgress={loading}
                            tableName={Label.ERASURE}
                            inputTestId={testIds.common.searchInput.itself}
                            clearButtonTestId={testIds.common.searchInput.clearButton}
                        />
                    </div>
                    <Tooltip
                        content={t("BulkSearchModal.title")}
                        placement="bottom"
                        hideOnClick={true}
                        trigger="mouseenter"
                    >
                        <button
                            type="button"
                            className={style.bulkSearchButton}
                            disabled={loading}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.currentTarget.blur();
                                setBulkModalOpen(true);
                            }}
                            data-testid={testIds.workArea.report.primaryView.table.bulkSearchButton}
                        >
                            <BulkSearchIcon color={props.theme.iconFillColor} />
                        </button>
                    </Tooltip>
                    <CustomDatePickerView
                        editable={true}
                        category={Category.REPORTS}
                        onUpdate={updateDateRange}
                        uuid={uuid}
                        defaultDateRange={defaultDateRange}
                        defaultPlaceholder={!!tableState.endDate}
                    />
                </div>
            </div>

            <ReportRecheck
                devices={findSelectedAutopilotIdentifiers(tableState.data)}
                showModal={showAutopilotRecheck}
                onClose={(licensesConsumed: number) => closeRecheckModal(licensesConsumed)}
            />
            <BulkReportDeletion
                selectedReports={findSelectedReportUuids(tableState.data)}
                deletionInvoked={bulkDeletionInvoked}
                onDeletionInvoked={setBulkDeletionInvoked}
                onDeletionDone={setBulkDeletionDone}
            />
            {shareDialogVisible && (
                <ShareReportDialog
                    reportUuids={findSelectedShareableReportUuids(tableState.data)}
                    languageCode={getLanguage().code}
                    hide={() => setShareDialogVisible(false)}
                />
            )}
            <SingleReportDeletion
                reportUuid={findSelectedReportUuids(tableState.data)[0]}
                deletionInvoked={singleDeletionInvoked}
                onDeletionInvoked={setSingleDeletionInvoked}
                onReportDeleted={props.onReportDelete}
            />
            <ExportReportsView
                isAllReportsView={true}
                columns={props?.viewDetails?.columns}
                filters={props?.viewDetails?.filters}
                startDate={tableState.startDate}
                searchQuery={tableState.searchQuery}
                endDate={tableState.endDate}
                exportAllButton={false}
                openExportDialog={openExportDialog}
                reportUuids={findSelectedReportUuids(tableState.data)}
                onSelectedReportsExport={setOpenExportDialog}
                hasOnlyBmsReports={onlyBmsReports}
                totalReportCount={findSelectedReportUuids(tableState.data).length}
            />

            <iframe
                ref={iframe}
                src={"about:blank"}
                style={{ visibility: "hidden", width: 0, height: 0 }}
                onLoad={() => setIframeLoaded(true)}
            />

            <div className={layoutStyle.tableWrapper}>
                <div className={layoutStyle.tableInnerWrapper}>
                    {tableWrapper}

                    {isReportSelected() && (
                        <ReportsTableActionBar
                            selectedCount={findSelectedReportUuids(tableState.data).length}
                            totalCount={tableState.data.length}
                            deselectAll={deselectAll}
                            selectAll={selectAll}
                            openExportModal={openExportModal}
                            openRegisterAssetModal={handleAssetsRegistration}
                            openPrintModal={openPrintPreview}
                            openShareModal={() => setShareDialogVisible(true)}
                            openAutopilotRecheckModal={() => setShowAutopilotRecheck(true)}
                            openDeleteModal={openDeleteModal}
                            isPrintDisabled={selectionCountOverPrintLimit}
                            isShareDisabled={selectionCountOverShareLimit}
                            selectionCountOverRecheckLimit={selectionCountOverRecheckLimit}
                            hasValidLicensesForRecheck={hasValidLicensesForRecheck}
                            isLoading={loading}
                            isReportWithAutopilotStateSelected={isReportWithAutopilotStateSelected()}
                            isErasureReportSelected={tableState.data.some(
                                (each) => each.checked && each.reportType === "ERASURE"
                            )}
                        />
                    )}
                </div>
            </div>

            {tableState.cursor.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                category: Category.REPORTS,
                                action: Action.LOAD_MORE,
                                label: Label.ALL,
                            });
                        }}
                        disabled={loading}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("ErasureReportsTable.loadMore")}
                    </button>
                ))}
            {modal}
            <Modal isOpen={printConfirmationDialog} hideModal={() => setPrintConfirmationDialog(false)}>
                <div className={style.warningContainer}>
                    <Warning color={props.theme.secondaryWarningBackgroundColor} />
                </div>
                <div className={style.printWarningTitle}>
                    <Heading tag={"h2"}>{t("ErasureReport.print.printWarningTitle")}</Heading>
                </div>
                <div className={style.warningContainer}>
                    {
                        <textarea
                            wrap="hard"
                            cols={60}
                            className={style.printWarningMessage}
                            defaultValue={t("ErasureReport.print.printWarningMessage", {
                                bmsReportCount: tableState.data.filter(
                                    (each) => each.checked && each.reportType === "DIAGNOSTIC"
                                ).length,
                                productName: deriveProductName("22"),
                            })}
                        ></textarea>
                    }
                </div>
                <div className={style.warningContainer}>
                    <button
                        className={buttonStyle.primaryButtonWithoutIcon}
                        onClick={() => {
                            setPrintConfirmationDialog(false);
                            printReports();
                        }}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("ErasureReport.print.printButton")}
                    </button>
                    <button
                        className={buttonStyle.secondaryButtonWithoutIcon}
                        onClick={() => setPrintConfirmationDialog(false)}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.cancel")}
                    </button>
                </div>
            </Modal>
            <ComponentsModal
                isOpen={registrationInProgressDialog}
                hideModal={() => setRegistrationInProgressDialog(false)}
                title={t("Notification.assetRegistrationNotifications.inProgressTitle")}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setRegistrationInProgressDialog(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <div className={classNames(style.processDialog, style.filters)}>
                    {t("ErasureReportsTable.registerAssets.processMultipleReportsMessage", {
                        count: selectedReportCountForRegistration,
                    })}
                </div>
            </ComponentsModal>
            <AlertModal
                title={t("Notification.assetRegistrationNotifications.failureTitle")}
                variant="Error"
                isOpen={registrationFailedDialog}
                hideModal={() => setRegistrationFailedDialog(false)}
                buttons={
                    <Button onClick={() => setRegistrationFailedDialog(false)} variant="Primary">
                        {t("Common.close")}
                    </Button>
                }
            >
                <TextBlock>{t("ErasureReportsTable.registerAssetsFailureMessage")}</TextBlock>
            </AlertModal>
            <Modal isOpen={iframeLoading} hideModal={() => setIframeLoading(false)} closeButton={true}>
                <LoadingIndicator />
                {tableState.data.filter((each) => each.checked).length > MAX_PRINT_REPORTS_LIMIT && (
                    <div className={style.warningContainer}>
                        {t("ErasureReport.print.maxPrintReportLabel", {
                            maxReportLimit: MAX_PRINT_REPORTS_LIMIT,
                        })}
                    </div>
                )}
            </Modal>
            <Modal
                isOpen={printError}
                hideModal={() => setPrintError(false)}
                modalTitle={t("ErasureReport.print.printErrorTitle")}
            >
                <ErrorMessage />
            </Modal>
            {bulkModalOpen && (
                <BulkSearchModal
                    onClose={() => setBulkModalOpen(false)}
                    initialValue={tableState.searchQuery}
                    onApply={applyBulkSearch}
                />
            )}
        </>
    );
}

export default connector(AllReportsTable);

/**
 * Extract autopilot identifier if the report contains an autopilot state.
 */
export function extractAutopilotDeviceIdentifier(report: Report): AutopilotDeviceIdentifier | undefined {
    const enrollementEntries = report.entries.filter((e) => e.path === Path.AUTOPILOT_STATE);
    if (enrollementEntries.length === 0) {
        return undefined;
    }
    const value = extractFirstNonBlankString(enrollementEntries[0].values);
    if (value == null) {
        return undefined;
    }

    let systemUuid: string | undefined;
    let offlineDeviceId: string | undefined;
    for (const each of report.entries) {
        if (each.path === Path.SYSTEM_OFFLINE_DEVICE_ID) {
            const value = extractFirstNonBlankString(each.values);
            if (value != null) {
                offlineDeviceId = value;
            }
            continue;
        }
        if (each.path === Path.SYSTEM_UUID) {
            const value = extractFirstNonBlankString(each.values);
            if (value != null) {
                systemUuid = value;
            }
        }
    }
    if (systemUuid == null && offlineDeviceId == null) {
        return undefined;
    }
    return { systemUuid, offlineDeviceId, reportUuid: report.uuid };
}

function extractFirstNonBlankString(values: string[] | number[]): string | undefined {
    const nonEmpty = values
        .filter((e) => e != null)
        .filter((e) => typeof e === "string")
        .map((e) => e.trim())
        .filter((e) => e !== "");
    if (nonEmpty.length > 0) {
        return nonEmpty[0];
    }
    return undefined;
}

/**
 * Omit those identifiers that don't introduce new values.
 */
export function filterUniqueAutopilotIdentifiers(
    identifiers: AutopilotDeviceIdentifier[]
): AutopilotDeviceIdentifier[] {
    const unique: AutopilotDeviceIdentifier[] = [];
    const seen = new Set<string>();
    for (const item of identifiers) {
        const nonNull = filterNonNull(item.systemUuid, item.offlineDeviceId);
        if (nonNull.every((e) => seen.has(e))) {
            continue;
        }
        nonNull.forEach((e) => seen.add(e));
        unique.push(item);
    }
    return unique;
}

function filterNonNull<T>(...items: (T | null | undefined)[]): T[] {
    const filtered = new Array<T>();
    for (const each of items) {
        if (each != null) {
            filtered.push(each);
        }
    }
    return filtered;
}
